import { useEffect, useRef, useState } from 'react';
import useStore from '../stores/store';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    Button,
    Flex,
    Text,
    Box,
    useColorModeValue,
    Icon,
    Link,
    Kbd
} from '@chakra-ui/react';
import { ImageIcon, ThemeSelector } from './TopRightOptions';
import {
    ArrowCircleLeft,
    ArrowCircleRight,
    ArrowSmallLeftIcon,
    ArrowSmallRightIcon,
    ExternalLinkIcon,
    MotionBox
} from './UiElements';
import { motion } from 'framer-motion';
import { ArrowCircleDownGreen } from './DropTabNewGroup';

const ProductTour = () => {
    const [showTour, setShowTour] = useState(true);
    const [currentStep, setStep] = useState(0);
    const updateUserData = useStore((state) => state.updateUserData);

    const nextStep = () => setStep(currentStep + 1);
    const prevStep = () => setStep(currentStep - 1);
    const retakeTour = () => setStep(1);
    const completeTour = () => {
        updateUserData({ tour: false });
        setShowTour(false);
    };
    const TOURSTEP = {
        0: (
            <Step0
                prevStep={prevStep}
                nextStep={nextStep}
                completeTour={completeTour}
            />
        ),
        1: (
            <Step1
                nextStep={nextStep}
                prevStep={prevStep}
                completeTour={completeTour}
            />
        ),
        2: (
            <Step2
                nextStep={nextStep}
                prevStep={prevStep}
                completeTour={completeTour}
            />
        ),
        3: (
            <Step3
                nextStep={nextStep}
                prevStep={prevStep}
                completeTour={completeTour}
            />
        ),
        4: (
            <Step4
                nextStep={nextStep}
                prevStep={prevStep}
                completeTour={completeTour}
            />
        ),
        5: (
            <Step5
                nextStep={nextStep}
                prevStep={prevStep}
                completeTour={completeTour}
            />
        ),
        6: <Step6 completeTour={completeTour} retakeTour={retakeTour} />
    };

    return <>{showTour && TOURSTEP[currentStep]}</>;
};

export default ProductTour;

const Step0 = ({ prevStep, nextStep, completeTour }) => {
    const borderBottom = useColorModeValue(
        '1px solid #e4eaf0',
        '1px solid #394146'
    );

    return (
        <Modal
            onClose={() => console.log('Closing onboarding')}
            closeOnOverlayClick={false}
            isOpen={true}
            isCentered
            size="md"
        >
            <ModalOverlay style={{ backdropFilter: 'blur(2px)' }} />
            <ModalContent
                rounded="16px"
                shadow={'xl'}
                bg="modalBg"
                motionProps={{
                    initial: { y: 120, rotate: 5 },
                    animate: {
                        y: 0,
                        rotate: 0
                        //transition: { duration: 0.12 }
                    },
                    exit: { y: 0, x: -120 }
                }}
            >
                <BodyContent
                    header="Welcome to tabExtend"
                    prevStep={false}
                    nextStep={nextStep}
                    nextButtonText="Show me around"
                    skipTour={completeTour}
                    step={0}
                >
                    <Text fontSize="20px" fontWeight={600} mb="8px" mt="16px">
                        Welcome to tabExtend
                    </Text>
                    <Text fontSize="14px" mb="10px" color="secondaryText">
                        tabExtend helps you manage tabs, add notes, and set
                        reminders all within easy-to-use Kanban views.
                    </Text>
                    <Text fontSize="14px" mb="20px" color="secondaryText">
                        Before we begin our app tour we start with light/dark
                        mode preferences (can also be changed later in
                        settings).
                    </Text>
                    <Box
                        borderTop={borderBottom}
                        borderBottom={borderBottom}
                        py="10px"
                    >
                        <ThemeSelector withTitles />
                    </Box>
                </BodyContent>
            </ModalContent>
        </Modal>
    );
};

const Step1 = ({ prevStep, nextStep, completeTour }) => {
    return (
        <StepWrapper top="0" left="0" h="100vh" w="72px" r="0">
            <ModalWrapper top="calc(50% - 96px)" left="84px" fromX={30}>
                <BodyContent
                    header="Your tabs"
                    prevStep={prevStep}
                    nextStep={nextStep}
                    nextButtonText="Next"
                    skipTour={completeTour}
                    step={1}
                >
                    <Text
                        fontSize="16px"
                        color="secondaryText"
                        my="12px"
                        fontWeight={500}
                    >
                        In the left sidebar, all your open tabs in the current
                        window are shown. Here you can manage your tabs and
                        tab-groups with ease.
                    </Text>
                </BodyContent>
            </ModalWrapper>
        </StepWrapper>
    );
};

const Step2 = ({ prevStep, nextStep, completeTour }) => {
    const isMac = navigator?.userAgent?.indexOf('Mac') !== -1;
    const cmd = isMac ? '⌘' : 'Ctrl';

    return (
        <StepWrapper top="0" left="0" h="0" w="0" r="0">
            <>
                <MotionBox
                    initial={{
                        opacity: 0,
                        scale: 0.75
                    }}
                    animate={{
                        opacity: 1,
                        scale: 1,
                        transition: { duration: 0.5 }
                    }}
                    display={'flex'}
                    pos={'absolute'}
                    left="96px"
                    top="74px"
                    h="calc(100vh - 88px)"
                    w="367px"
                    borderRadius="16px"
                    zIndex="1"
                    border="2px solid #41E18B"
                    bg="rgb(65, 225, 139, 0.1)"
                    alignItems={'center'}
                >
                    <MotionBox
                        initial={{
                            scale: 0.75,
                            y: -50,
                            opacity: 0,
                            originY: 1
                        }}
                        animate={{
                            scale: 1.75,
                            y: 0,
                            opacity: 0.8,
                            originY: 0,
                            transition: { type: 'spring', duration: 1 }
                        }}
                        m="auto"
                        display="flex"
                        alignItems="center"
                        pb="32px"
                    >
                        <Icon
                            m="auto"
                            as={ArrowCircleDownGreen}
                            fontSize="54px"
                            color="#41E18B"
                        />
                    </MotionBox>
                </MotionBox>
                <ModalWrapper
                    top="calc(50vh - 96px)"
                    left="348px"
                    modalW="422px"
                >
                    <BodyContent
                        header="Drop-zone"
                        prevStep={prevStep}
                        nextStep={nextStep}
                        nextButtonText="Next"
                        skipTour={completeTour}
                        step={2}
                    >
                        <Text
                            fontSize="16px"
                            mt="12px"
                            mb="8px"
                            color="secondaryText"
                            fontWeight={500}
                        >
                            To create your first group, you can drag and drop
                            one or more tabs into the shown drop-zone.
                        </Text>
                        <Text
                            fontSize="16px"
                            mb="12px"
                            color="secondaryText"
                            fontWeight={500}
                        >
                            You can then drag more tabs into the group or create
                            another. You can also use <Kbd>{cmd}</Kbd> +{' '}
                            <Kbd>G</Kbd> to create new groups.
                        </Text>
                    </BodyContent>
                </ModalWrapper>
            </>
        </StepWrapper>
    );
};

const Step3 = ({ prevStep, nextStep, completeTour }) => {
    return (
        <StepWrapper top="22px" left="18px" h="32px" w="32px" r="11px">
            <ModalWrapper top="32px" left="36px">
                <BodyContent
                    header="Window menu"
                    prevStep={prevStep}
                    nextStep={nextStep}
                    nextButtonText="Next"
                    skipTour={completeTour}
                    step={3}
                >
                    <Text
                        fontSize="16px"
                        color="secondaryText"
                        my="12px"
                        fontWeight={500}
                    >
                        Click here to perform actions on all tabs:
                    </Text>
                    <Flex mt="8px">
                        <ImageIcon
                            name="save_close"
                            hoverItem={false}
                            mr="8px"
                        />
                        <Text
                            fontSize="16px"
                            color="secondaryText"
                            my="auto"
                            fontWeight={500}
                        >
                            Save all tabs into a new group
                        </Text>
                    </Flex>
                    <Flex mt="12px">
                        <ImageIcon
                            name="copy_tabs"
                            hoverItem={false}
                            mr="8px"
                        />
                        <Text
                            fontSize="16px"
                            color="secondaryText"
                            my="auto"
                            fontWeight={500}
                        >
                            Save all into an existing group
                        </Text>
                    </Flex>
                    <Flex mt="12px">
                        <ImageIcon
                            name="clear_tabs"
                            hoverItem={false}
                            mr="8px"
                        />
                        <Text
                            fontSize="16px"
                            color="secondaryText"
                            my="auto"
                            fontWeight={500}
                        >
                            Close all tabs
                        </Text>
                    </Flex>
                </BodyContent>
            </ModalWrapper>
        </StepWrapper>
    );
};

const Step4 = ({ prevStep, nextStep, completeTour }) => {
    return (
        <StepWrapper top="22px" left="92px" h="32px" w="326px" r="8px">
            <ModalWrapper top="48px" left="32px">
                <BodyContent
                    header="Window menu"
                    prevStep={prevStep}
                    nextStep={nextStep}
                    nextButtonText="Next"
                    skipTour={completeTour}
                    step={4}
                >
                    <Text
                        fontSize="16px"
                        color="secondaryText"
                        my="12px"
                        fontWeight={500}
                    >
                        Groups are sorted into editable categories, which are
                        then sorted into workspaces.
                    </Text>
                    <Text
                        fontSize="16px"
                        color="secondaryText"
                        my="12px"
                        fontWeight={500}
                    >
                        Use <Kbd>1</Kbd>,<Kbd>2</Kbd>,<Kbd>3</Kbd> etc to
                        quickly jump between categories and <Kbd>Space</Kbd> to
                        see all workspaces.
                    </Text>
                </BodyContent>
            </ModalWrapper>
        </StepWrapper>
    );
};

const Step5 = ({ prevStep, nextStep, completeTour }) => {
    const isMac = navigator?.userAgent?.indexOf('Mac') !== -1;
    const cmd = isMac ? '⌘' : 'Ctrl';

    return (
        <StepWrapper top="22px" right="31px" h="32px" w="200px" r="8px">
            <ModalWrapper top="48px" left="-163px">
                <BodyContent
                    header="Window menu"
                    prevStep={prevStep}
                    nextStep={nextStep}
                    nextButtonText="Next"
                    skipTour={completeTour}
                    step={5}
                >
                    <Text
                        fontSize="16px"
                        color="secondaryText"
                        mt="12px"
                        fontWeight={500}
                    >
                        In the top-right corner, you'll find:
                    </Text>
                    <Text
                        fontSize="16px"
                        color="secondaryText"
                        fontWeight={500}
                        my="4px"
                    >
                        • Search <Kbd>{cmd}</Kbd> + <Kbd>K</Kbd>
                    </Text>
                    <Text
                        fontSize="16px"
                        color="secondaryText"
                        fontWeight={500}
                        my="4px"
                    >
                        • Action menu
                    </Text>
                    <Text
                        fontSize="16px"
                        color="secondaryText"
                        fontWeight={500}
                        my="4px"
                    >
                        • Reminders overview
                    </Text>
                    <Text
                        fontSize="16px"
                        color="secondaryText"
                        fontWeight={500}
                        my="4px"
                    >
                        • Share
                    </Text>
                    <Text
                        fontSize="16px"
                        color="secondaryText"
                        fontWeight={500}
                        my="4px"
                    >
                        • User details
                    </Text>
                </BodyContent>
            </ModalWrapper>
        </StepWrapper>
    );
};

const Step6 = ({ retakeTour, completeTour }) => {
    const borderBottom = useColorModeValue(
        '1px solid #e4eaf0',
        '1px solid #394146'
    );

    return (
        <Modal
            onClose={() => console.log('Closing onboarding')}
            closeOnOverlayClick={false}
            isOpen={true}
            isCentered
            size="md"
        >
            <ModalOverlay style={{ backdropFilter: 'blur(2px)' }} />
            <ModalContent
                rounded="16px"
                shadow={'xl'}
                bg="modalBg"
                motionProps={{
                    initial: { y: -24, scale: 0.5 },
                    animate: {
                        y: 0,
                        scale: 1
                        //transition: { duration: 0.24, type: 'spring' }
                    }
                }}
            >
                <BodyContent
                    header="Done"
                    prevStep={false}
                    nextStep={completeTour}
                    nextButtonText="Get started!" //"Let's get organized!"
                    skipTour={retakeTour}
                    step={6}
                >
                    <Text fontSize="20px" fontWeight={600} mb="8px" mt="16px">
                        That's it! 🎉
                    </Text>
                    <Text fontSize="14px" mb="4px" color="secondaryText">
                        Find more tips in our guide, including how to save
                        text-snippets from websites, creating/sharing
                        workspaces, multi-select, shortcuts, and more at:
                    </Text>
                    <Link
                        mb="44px"
                        fontSize="14px"
                        href="https://www.tabextend.com/guide"
                        target="_blank"
                        rel="noopener noreferrer"
                        display={'flex'}
                        alignItems={'center'}
                    >
                        <u style={{ marginRight: '2px' }}>
                            www.tabextend.com/guide
                        </u>
                        <Icon
                            mr="8px"
                            as={ExternalLinkIcon}
                            fontSize="20px"
                            ml="4px"
                            mt="2px"
                        />
                    </Link>
                    {/* <Box
                        borderTop={borderBottom}
                        borderBottom={borderBottom}
                        py="10px"
                    ></Box> */}
                </BodyContent>
            </ModalContent>
        </Modal>
    );
};

const ModalWrapper = ({
    top = '',
    bottom = '',
    left,
    modalW = '364px',
    children,
    fromX = 0,
    fromY = 0
}) => {
    return (
        <Box
            initial={{ x: fromX, y: fromY, scale: fromX === 30 ? 1.125 : 1 }}
            animate={{ x: 0, y: 0, scale: 1 }}
            as={motion.div}
            layoutId="modalContent"
            layout="position"
            rounded="16px"
            shadow={'xl'}
            bg="modalBg"
            pos="absolute"
            top={top}
            bottom={bottom}
            left={left}
            w={modalW}
            h={'auto'}
            zIndex="2"
        >
            {children}
        </Box>
    );
};

const BodyContent = ({
    header,
    prevStep,
    skipTour,
    nextStep,
    nextButtonText,
    step,
    children
}) => {
    const btnStyle = {
        rounded: '10px',
        size: 'md',
        transition: '0.12s',
        _active: { transform: 'scale(0.9875)' },
        lineHeight: '2px'
    };

    const actionSteps = 6;
    let progress = (step / actionSteps) * 100;

    const buttonRef = useRef(null);

    useEffect(() => {
        buttonRef.current?.focus();
    }, []);

    return (
        <Box p="16px 24px">
            {step !== 0 && (
                <Flex>
                    <ProgressCircle progress={progress} />
                    <Text
                        color="#54a5fe"
                        m="auto 0 auto 8px"
                        fontSize="14px"
                        fontWeight={700}
                    >
                        Step {step + '/' + actionSteps}
                    </Text>
                </Flex>
            )}
            {children}
            <Flex w="100%" mt="24px">
                {prevStep && (
                    <Button onClick={prevStep} mr="8px" {...btnStyle}>
                        <ArrowCircleLeft />
                    </Button>
                )}
                <Button onClick={skipTour} mr="8px" {...btnStyle} ml="auto">
                    {step === 6 ? 'Retake tour' : 'Skip tour'}
                </Button>
                <Button
                    ref={buttonRef}
                    onClick={nextStep}
                    {...btnStyle}
                    color="#FFF"
                    backgroundImage="linear-gradient(145deg, #5DABFE 0%, #3391FF 100%)"
                    _hover={{
                        backgroundImage:
                            'linear-gradient(145deg, #3592FE 0%, #2E7EDB 100%)',
                        boxShadow: '0 8px 16px -8px rgba(49, 144, 255,0.3)'
                    }}
                    boxShadow="0 12px 18x -10px rgba(49, 144, 255,0.35)"
                    transition="all 0.12s cubic-bezier(.374,.019,.035,1.861)"
                    pl="12px"
                >
                    <ArrowCircleRight />
                    {nextButtonText}
                </Button>
            </Flex>
        </Box>
    );
};

const StepWrapper = ({ children, top, left = null, right = null, h, w, r }) => {
    //useLockBody();
    return (
        <Flex
            pos="fixed"
            top="0"
            left="0"
            width="100vw"
            height="100vh"
            backgroundColor="transparent"
            zIndex="1002"
            alignItems="center"
        >
            <motion.div
                initial={
                    {
                        //x: top === '0' ? 30 : 0
                        // filter: 'blur(1px)'
                    }
                }
                animate={
                    {
                        //filter: 'blur(0px)',
                        //x: 0
                    }
                }
                style={{
                    position: 'absolute',
                    top: top,
                    left: left,
                    right: right,
                    height: h,
                    width: w,
                    borderRadius: r,
                    boxShadow: 'rgb(0 0 0 / 60%) 0px 0px 0px 99999px'
                }}
            >
                {children}
            </motion.div>
        </Flex>
    );
};

const ProgressCircle = ({ progress }) => {
    const radius = 8;
    const strokeWidth = 3;
    const adjustedSize = (radius + strokeWidth) * 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
        <svg height={adjustedSize} width={adjustedSize}>
            <circle
                stroke="var(--chakra-colors-te2)"
                fill="transparent"
                strokeWidth={strokeWidth}
                r={radius}
                cx={radius + strokeWidth}
                cy={radius + strokeWidth}
            />
            <circle
                stroke="#54a5fe"
                fill="transparent"
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                r={radius}
                cx={radius + strokeWidth}
                cy={radius + strokeWidth}
                transform={`rotate(-90 ${radius + strokeWidth} ${
                    radius + strokeWidth
                })`}
            />
        </svg>
    );
};
